<div class="w-full p-6 max-w-3xl">
    <form [formGroup]="venueForm" (ngSubmit)="onSubmit()">
        <!-- Section -->
        <div class="w-full">
            <div class="text-xl" *ngIf="isEditMode">Update Venue</div>
            <div class="text-xl" *ngIf="!isEditMode">New Venue</div>
        </div>
        <div class="mb-10 mt-11 border-t"></div>
        <div class="mt-8 grid w-full gap-6 sm:grid-cols-4">
            <!-- Name -->
            <div class="sm:col-span-3">
                <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                    <mat-label>Venue Name</mat-label>
                    <input formControlName="name" matInput required />
                    <mat-error
                        *ngIf="venueForm.get('name')?.hasError('required')"
                    >
                        Venue Name is required
                    </mat-error>
                </mat-form-field>
            </div>

            <div class="sm:col-span-1">
                <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                    <mat-label>Status</mat-label>
                    <mat-icon
                        class="icon-size-3"
                        [style.color]="
                            venueForm.get('status')?.value === 'active'
                                ? '#2E9B11'
                                : '#F33779'
                        "
                        [svgIcon]="'mat_solid:circle'"
                        matPrefix
                    ></mat-icon>
                    <mat-select [formControlName]="'status'">
                        <mat-option [value]="'active'">Active</mat-option>
                        <mat-option [value]="'inactive'">In-active</mat-option>
                    </mat-select>
                </mat-form-field>
            </div>

            <!-- Venue location -->
            <div class="sm:col-span-4">
                <app-venue-location-picker
                    [disabled]="isLoading"
                    [value]="location"
                    [venueName]="venueForm.get('name')?.value || ''"
                    (valueChange)="onLocationChange($event)"
                ></app-venue-location-picker>
            </div>

            <!-- Timing Section -->
            <div class="sm:col-span-2 font-semibold">Timing</div>
            <div class="text-center sm:col-span-1 font-semibold">Start</div>
            <div class="text-center sm:col-span-1 font-semibold">End</div>

            <!-- Days Timing -->
            <div class="col-span-4">
                <div
                    *ngFor="let day of timing; let i = index"
                    class="grid grid-cols-4"
                >
                    <div class="col-span-2 flex items-center">
                        <mat-checkbox
                            name="checked-{{ i }}"
                            [color]="'primary'"
                            [(ngModel)]="day.checked"
                            [ngModelOptions]="{ standalone: true }"
                        >
                            {{ day.name }}
                        </mat-checkbox>
                    </div>
                    <div class="col-span-1">
                        <mat-form-field>
                            <mat-select
                                name="start-time-{{ i }}"
                                [(value)]="day.startTime"
                                (selectionChange)="
                                    startTimeChange(
                                        day.startTime,
                                        day.endTime,
                                        i
                                    )
                                "
                            >
                                <mat-option
                                    *ngFor="let t of times.slice(0, -1)"
                                    [value]="t"
                                >
                                    {{ t }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                    <div class="col-span-1">
                        <mat-form-field>
                            <mat-select
                                name="end-time-{{ i }}"
                                [(value)]="day.endTime"
                            >
                                <mat-option
                                    *ngFor="let t of times.slice(1)"
                                    [value]="t"
                                    [disabled]="isDisabled(day.startTime, t)"
                                >
                                    {{ t }}{{ isNextDayEndTime(day.startTime, t) ? " (next day)" : "" }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>
                    </div>
                    <div class="col-span-4 text-right" *ngIf="i === 0">
                        <button
                            type="button"
                            class="apply-all-btn"
                            mat-button
                            (click)="applyToAll()"
                        >
                            Apply to all
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Rules Section -->
        <div class="mt-8 w-full">
            <div class="text-lg font-semibold mb-4">Rules</div>
            <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                <textarea
                    matInput
                    formControlName="rules"
                    rows="5"
                    placeholder="Enter rules or guidelines for players (e.g., Players must arrive 10 minutes before the match)"
                ></textarea>
                <mat-hint
                    >Provide venue-specific rules or restrictions</mat-hint
                >
            </mat-form-field>
        </div>

        <div class="mb-10 mt-5 border-t"></div>

        <!-- Cancellation Policy Section -->
        <div class="mt-8 w-full">
            <div class="flex items-center justify-between mb-4">
                <div class="text-lg font-semibold">Cancellation Policy</div>
                <mat-checkbox
                    [color]="'primary'"
                    [(ngModel)]="cancellationPolicy.enabled"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="onCancellationPolicyToggle()"
                >
                    Enable
                </mat-checkbox>
            </div>

            <!-- Template Selector -->
            <div *ngIf="cancellationPolicy.enabled" class="mb-4">
                <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                    <mat-label>Select Template</mat-label>
                    <mat-select
                        [value]="selectedCancellationTemplate"
                        (selectionChange)="
                            applyCancellationTemplate($event.value)
                        "
                        [disabled]="isLoadingTemplates"
                        [compareWith]="compareTemplates"
                    >
                        <mat-option
                            *ngFor="let template of cancellationTemplates"
                            [value]="template"
                        >
                            {{ template.name }}
                            <span
                                *ngIf="template.isDefault"
                                class="text-xs text-gray-500"
                                >(Default)</span
                            >
                        </mat-option>
                    </mat-select>
                    <mat-hint>Select a cancellation policy template</mat-hint>
                </mat-form-field>

                <!-- Display selected template rules (read-only) -->
                <div
                    *ngIf="
                        selectedCancellationTemplate &&
                        cancellationPolicy.rules.length > 0
                    "
                    class="mt-4 p-4 bg-gray-50 rounded-lg"
                >
                    <div class="text-sm font-semibold mb-2">Policy Rules:</div>
                    <div class="space-y-2">
                        <div
                            *ngFor="let rule of cancellationPolicy.rules"
                            class="text-sm"
                        >
                            • Cancel
                            <strong>{{ rule.hoursBeforeBooking }} hours</strong>
                            or more before booking:
                            <strong>{{ rule.refundPercentage }}%</strong> refund
                        </div>
                        <div
                            *ngIf="
                                cancellationPolicy.defaultRefundPercentage !==
                                    undefined &&
                                cancellationPolicy.defaultRefundPercentage !==
                                    null
                            "
                            class="text-sm"
                        >
                            • Default refund (doesn't match any rule):
                            <strong
                                >{{
                                    cancellationPolicy.defaultRefundPercentage
                                }}%</strong
                            >
                        </div>
                        <div
                            *ngIf="
                                cancellationPolicy.rules.length === 0 &&
                                cancellationPolicy.defaultRefundPercentage === 0
                            "
                            class="text-sm"
                        >
                            • <strong>No refund</strong> policy
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mb-10 mt-5 border-t"></div>

        <!-- Privacy Policy Section -->
        <div class="mt-8 w-full">
            <div class="text-lg font-semibold mb-4">Privacy Policy</div>

            <!-- Template Selector -->
            <mat-form-field class="w-full mb-4" [subscriptSizing]="'dynamic'">
                <mat-label>Select Template</mat-label>
                <mat-select
                    [value]="selectedPrivacyPolicyTemplate"
                    (selectionChange)="applyPrivacyPolicyTemplate($event.value)"
                    [disabled]="isLoadingTemplates"
                    [compareWith]="compareTemplates"
                >
                    <mat-option
                        *ngFor="let template of privacyPolicyTemplates"
                        [value]="template"
                    >
                        {{ template.name }}
                        <span
                            *ngIf="template.isDefault"
                            class="text-xs text-gray-500"
                            >(Default)</span
                        >
                    </mat-option>
                </mat-select>
                <mat-hint>Select a privacy policy template</mat-hint>
            </mat-form-field>

            <!-- Display privacy policy content (read-only when template is selected) -->
            <mat-form-field class="w-full" [subscriptSizing]="'dynamic'">
                <mat-label>Privacy Policy</mat-label>
                <textarea
                    matInput
                    formControlName="privacyPolicy"
                    rows="10"
                    placeholder="Select a template to load privacy policy content"
                    [readonly]="!!selectedPrivacyPolicyTemplate"
                ></textarea>
                <mat-hint *ngIf="selectedPrivacyPolicyTemplate">
                    Policy loaded from template. Edit the template in Settings
                    to modify.
                </mat-hint>
                <mat-hint *ngIf="!selectedPrivacyPolicyTemplate">
                    Select a template or enter custom privacy policy content.
                </mat-hint>
            </mat-form-field>
        </div>

        <div class="mb-10 mt-5 border-t"></div>

        <!-- Actions -->
        <div
            class="sticky bottom-0 z-10 flex items-center justify-end border-t bg-white py-4"
        >
            <button
                class="ml-4 pr-20 pl-20"
                mat-flat-button
                type="submit"
                [color]="'primary'"
                [disabled]="venueForm.invalid || isLoading"
            >
                <span *ngIf="!isLoading && !isEditMode">Save</span>
                <span *ngIf="!isLoading && isEditMode">Update</span>
                <mat-spinner *ngIf="isLoading" diameter="20"></mat-spinner>
            </button>
        </div>
        <div *ngIf="errors.length > 0">
            <mat-error class="text-center">
                {{ this.errors[0] }}
            </mat-error>
        </div>
    </form>
</div>
